import {
  FILTER_VALUE_SEPARATOR,
  RANGE_FILTER_SEPARATOR,
} from '@aibees/elephant/constants/filters';
import {
  MetricDepartmentItem,
  MetricGrowthItem,
} from '@aibees/elephant/feature/search-table-company/hooks/types';
import { SearchBrowseParams } from '@aibees/elephant/types';
import { Maybe } from '@aibees/shared/definition';
import { INNER_SEPARATOR, ITEM_SEPARATOR } from './config';
import { getItemQueryType, QUERY_TYPE_BACKEND_OPTION } from './query-type';
import { TimeFrames } from '../time-frame-filter/components/time-frame-select';

export function buildDraft(
  params: Partial<SearchBrowseParams>,
  field: string,
  itemIndex: number,
  valueIndex: number,
  value: Maybe<string>
): Maybe<string> {
  const currentParams = params[field];

  if (!currentParams) {
    const newItemValues: (string | undefined)[] = [];
    newItemValues[valueIndex] = value;
    return newItemValues.join(INNER_SEPARATOR);
  }

  const items = currentParams.split(ITEM_SEPARATOR);
  const itemToUpdate = items[itemIndex] ?? INNER_SEPARATOR;
  const itemValues: (string | undefined)[] =
    itemToUpdate.split(INNER_SEPARATOR);

  itemValues[valueIndex] = value;

  items[itemIndex] = itemValues.join(INNER_SEPARATOR);

  const draft = items.join(ITEM_SEPARATOR);
  return draft && draft !== '' ? draft : undefined;
}

export function getItemsArray(
  params: Partial<SearchBrowseParams>,
  field: string
): string[] {
  const currentParams = params[field];
  return currentParams ? currentParams.split(ITEM_SEPARATOR) : [];
}

export function clearEmployeeByDepartmentItem(
  params: Partial<SearchBrowseParams>,
  field: string,
  index: number
): Maybe<string> {
  const currentParam = params[field];
  if (!currentParam) return;
  const items = currentParam.split(ITEM_SEPARATOR);
  if (!items[index]) return;

  const filteredItems = items.filter((_, i) => i !== index);

  if (filteredItems.length === 0) return undefined;

  return filteredItems.join(ITEM_SEPARATOR);
}

export function clearDepartmentValue(
  params: Partial<SearchBrowseParams>,
  field: string,
  valueToRemove: string
): Maybe<string> {
  const currentParam = params[field];
  if (!currentParam) return;
  const items = currentParam.split(ITEM_SEPARATOR);

  // Remove `valueToRemove` from each row's dep slot (which may hold multiple
  // values joined by FILTER_VALUE_SEPARATOR after multi-select was enabled).
  // When a row's last dep is removed, drop the whole row — a row without a
  // department is not an active filter, and leaving it behind would leak stale
  // `|range` rows into the URL.
  const updatedItems: string[] = [];
  for (const item of items) {
    const segments = item.split(INNER_SEPARATOR);
    const depValues =
      segments[0]?.split(FILTER_VALUE_SEPARATOR).filter(Boolean) ?? [];
    const filtered = depValues.filter((v) => v !== valueToRemove);
    if (filtered.length === depValues.length) {
      updatedItems.push(item);
      continue;
    }
    if (filtered.length === 0) continue;
    segments[0] = filtered.join(FILTER_VALUE_SEPARATOR);
    updatedItems.push(segments.join(INNER_SEPARATOR));
  }

  if (updatedItems.length === 0) return undefined;
  return updatedItems.join(ITEM_SEPARATOR);
}

/**
 * Keep only rows that carry the segments required to trigger a search:
 *  - EBD: dep + range (min OR max).
 *  - Headcount Growth: dep + range + timeFrame.
 * The trailing queryType segment is ignored — it's metadata, not input.
 */
export function filteredDepartmentItemsWithFullValues(
  department: string,
  headcountGrowth?: boolean
): string[] {
  const separated = department.split(ITEM_SEPARATOR);
  return separated.filter((item) => {
    const segments = item.split(INNER_SEPARATOR);
    const deps = segments[0];
    const [start, end] = segments[1]?.split(RANGE_FILTER_SEPARATOR) ?? [];
    const hasDepartmentAndRange = !!deps && (!!start || !!end);
    const hasTimeFrame = !!segments[2];
    return (
      (!headcountGrowth && hasDepartmentAndRange) ||
      (!!headcountGrowth && hasDepartmentAndRange && hasTimeFrame)
    );
  });
}

// Shared parsing for both metric variants. `segmentCount` tells
// `getItemQueryType` which slot holds the queryType (after the input segments):
// 2 for EBD (dep + range), 3 for Headcount Growth (dep + range + timeFrame).
function parseMetricRow(
  item: string,
  segmentCount: number
): MetricDepartmentItem {
  const segments = item.split(INNER_SEPARATOR);
  const deps = segments[0];
  const [start, end] = segments[1]?.split(RANGE_FILTER_SEPARATOR) ?? [];
  const queryType = getItemQueryType(item, segmentCount);
  return {
    function: deps.split(FILTER_VALUE_SEPARATOR),
    start: start
      ? Number(start)
      : !!end && Math.sign(Number(end)) === 1 // if end is positive and start is not present, set start to 0
      ? 0
      : undefined,
    end: end ? Number(end) : undefined,
    option: QUERY_TYPE_BACKEND_OPTION[queryType],
  };
}

export function addEmployeeMetric(department: string): MetricDepartmentItem[] {
  return filteredDepartmentItemsWithFullValues(department).map((item) =>
    parseMetricRow(item, 2)
  );
}

export function addGrowthMetric(department: string): MetricGrowthItem[] {
  return filteredDepartmentItemsWithFullValues(department, true).map(
    (item) => ({
      ...parseMetricRow(item, 3),
      timeFrame: item.split(INNER_SEPARATOR)[2] ?? TimeFrames.TWELVE,
    })
  );
}
